// Lightweight LEGO-colored confetti - no dependencies.
// Fires a burst of tiny rotating "bricks" (rounded rectangles with a stud dot)
// on a temporary full-screen canvas. Respects prefers-reduced-motion.

const LEGO_COLORS = ['#e3000b', '#ffcf00', '#228be6', '#00af4d', '#fd7e14', '#ffffff'];

function reducedMotion() {
  return typeof window !== 'undefined'
    && window.matchMedia
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

export function fireConfetti({ particleCount = 80, origin = { x: 0.5, y: 0.4 }, spread = 70, power = 12, duration = 2200 } = {}) {
  if (typeof window === 'undefined' || reducedMotion()) return;

  const canvas = document.createElement('canvas');
  const dpr = window.devicePixelRatio || 1;
  canvas.width = window.innerWidth * dpr;
  canvas.height = window.innerHeight * dpr;
  Object.assign(canvas.style, {
    position: 'fixed', inset: '0', width: '100%', height: '100%',
    pointerEvents: 'none', zIndex: '9999',
  });
  document.body.appendChild(canvas);
  const ctx = canvas.getContext('2d');
  ctx.scale(dpr, dpr);

  const w = window.innerWidth;
  const h = window.innerHeight;
  const spreadRad = (spread * Math.PI) / 180;

  const particles = Array.from({ length: particleCount }, () => {
    const angle = -Math.PI / 2 + (Math.random() - 0.5) * spreadRad;
    const velocity = power * (0.5 + Math.random() * 0.8);
    return {
      x: origin.x * w,
      y: origin.y * h,
      vx: Math.cos(angle) * velocity,
      vy: Math.sin(angle) * velocity,
      rotation: Math.random() * Math.PI * 2,
      vr: (Math.random() - 0.5) * 0.3,
      width: 9 + Math.random() * 5,
      height: 6 + Math.random() * 3,
      color: LEGO_COLORS[Math.floor(Math.random() * LEGO_COLORS.length)],
    };
  });

  const start = performance.now();
  let raf;

  const tick = (now) => {
    const elapsed = now - start;
    const progress = elapsed / duration;
    ctx.clearRect(0, 0, w, h);

    if (progress >= 1) {
      cancelAnimationFrame(raf);
      canvas.remove();
      return;
    }

    const alpha = progress > 0.7 ? 1 - (progress - 0.7) / 0.3 : 1;
    for (const p of particles) {
      p.vy += 0.35; // gravity
      p.vx *= 0.985; // drag
      p.vy *= 0.985;
      p.x += p.vx;
      p.y += p.vy;
      p.rotation += p.vr;

      ctx.save();
      ctx.globalAlpha = alpha;
      ctx.translate(p.x, p.y);
      ctx.rotate(p.rotation);
      ctx.fillStyle = p.color;
      ctx.beginPath();
      if (typeof ctx.roundRect === 'function') {
        ctx.roundRect(-p.width / 2, -p.height / 2, p.width, p.height, 2);
      } else {
        ctx.rect(-p.width / 2, -p.height / 2, p.width, p.height);
      }
      ctx.fill();
      // stud dot to make the confetti read as a tiny brick
      ctx.fillStyle = 'rgba(255, 255, 255, 0.45)';
      ctx.beginPath();
      ctx.arc(0, -p.height / 4, p.height / 5, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    }

    raf = requestAnimationFrame(tick);
  };

  raf = requestAnimationFrame(tick);
}

// Small burst - a single bag fully checked.
export function fireBagConfetti() {
  fireConfetti({ particleCount: 45, origin: { x: 0.5, y: 0.45 }, power: 9, duration: 1800 });
}

// Big celebration - the whole set fully checked.
export function fireSetConfetti() {
  fireConfetti({ particleCount: 90, origin: { x: 0.5, y: 0.35 }, power: 13 });
  setTimeout(() => fireConfetti({ particleCount: 60, origin: { x: 0.15, y: 0.55 }, power: 11 }), 200);
  setTimeout(() => fireConfetti({ particleCount: 60, origin: { x: 0.85, y: 0.55 }, power: 11 }), 350);
}
